import { FC, useMemo, useState } from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import SettingTitle from "../component/SettingTitle";
import CustomInput from "@/components/UI/CustomInput/CustomInput";
import theme from "@/theme";
import HeartSvg from "@/assets/svg/HeartSvg";

import { styles } from "./stylesLikePrice";
import { useAppSelector } from "@/hooks/useAppSelector";
import PremiumSubscriptionModal from "@/components/UI/modal/PremiumSubscriptionModal/PremiumSubscriptionModal";
import CustomToast from "@/components/UI/CustomToast/CustomToast";
import { useGetUserSettingsQuery, useUpdateUserSettingsMutation } from "@/store/api/authApi";
import { formatMoney } from "@/helpers/formatMoney";

const MAX_PRICE_DIGITS = 15;
const MIN_USD = 0.25;
const LikePrice: FC = () => {
  const [price, setPrice] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const { subscriptionStatus } = useAppSelector((state) => state.auth);
  const hasPremium = subscriptionStatus === "ACTIVE";
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; success?: boolean } | null>(null);
  const [updateSettings] = useUpdateUserSettingsMutation();
  const { data: userSettings } = useGetUserSettingsQuery();
  const likePrice = userSettings?.customLikeAmountUsdt;
  const openPremiumModal = () => setPremiumModalOpen(true);
  const closePremiumModal = () => setPremiumModalOpen(false);
  const priceError = useMemo(() => {
    if (!price) return null;
    const value = parseInt(price);
    if (isNaN(value)) return "Please enter a valid number";
    if (value < MIN_USD) return `Minimum amount is ${MIN_USD} USDC`;
    return null;
  }, [price]);

  const isValid = useMemo(() => {
    if (!price.trim()) return false;
    const value = parseFloat(price);
    return !isNaN(value) && value >= MIN_USD;
  }, [price]);
  const normalizePrice = (value: string) => {
    let normalized = value.replace(/[^0-9.]/g, "");

    const parts = normalized.split(".");
    if (parts.length > 2) {
      normalized = parts[0] + "." + parts.slice(1).join("");
    }

    return normalized.slice(0, MAX_PRICE_DIGITS);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const normalized = normalizePrice(e.target.value);
    setPrice(normalized);
  };
  const handleSubmit = async () => {
    if (!price) return;

    try {
      await updateSettings({
        customLikeAmountUsdt: Number(price),
      }).unwrap();

      setToast({ message: "Like price updated successfully", success: true });
    } catch (e) {
      setToast({ message: "Failed to update like price", success: false });
    }
  };
  const isDisabled = !isValid || !hasPremium;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      {toast && (
        <CustomToast
          message={toast.message}
          success={toast.success}
          onClose={() => setToast(null)}
        />
      )}
      <PremiumSubscriptionModal
        open={premiumModalOpen}
        onClose={closePremiumModal}
        setToast={setToast}
      />

      <SettingTitle title="Set or edit price of your like" back isActive={true} />
      <Box
        sx={{
          maxWidth: 544,
          maxHeight: 116,
          opacity: 1,
          gap: "10px",
          paddingTop: "10px",
          paddingLeft: "16px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            maxWidth: "528px",
            maxHeight: "80px",
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-end",
            gap: "10px",
          }}
        >
          <CustomInput
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            label="Like price"
            placeholder=" "
            value={price}
            onChange={handleChange}
            startAdornment={
              <Box sx={styles.startAdornmentBox}>
                <HeartSvg width={18} height={18} />
                <Typography sx={styles.equalSign}>=</Typography>
              </Box>
            }
            endAdornment={
              <Box
                sx={{
                  position: "absolute",
                  top: 16,
                  left: 60 + price.length * 9.8,
                  display: "flex",
                  flexDirection: "row",
                  gap: "6px",
                }}
              >
                <Typography
                  sx={{
                    ...styles.satsText,
                    color:
                      isFocused || price.length
                        ? theme.palette.custom.blueGray900
                        : theme.palette.custom.blueGray400,
                  }}
                >
                  USDC
                </Typography>
                {/* <Typography sx={styles.usdText}>
                  {price ? `$${formatWithCommas(Number(price).toFixed(2))}` : ""}
                </Typography> */}
                {priceError && (
                  <Typography
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      color: "#E62A3B",
                      fontSize: "12px",
                      fontWeight: 400,
                      lineHeight: "16px",
                    }}
                  >
                    {priceError}
                  </Typography>
                )}
              </Box>
            }
          />
          <Tooltip
            disableHoverListener={hasPremium}
            placement="top"
            title={
              !hasPremium ? (
                <Typography sx={{ lineHeight: "18px" }}>
                  Available only for{" "}
                  <Button
                    onClick={openPremiumModal}
                    sx={{
                      color: theme.palette.custom.mainWhite,
                      textDecoration: "underline",
                      textTransform: "none",
                      padding: 0,
                      minWidth: "auto",
                    }}
                  >
                    Premium
                  </Button>
                </Typography>
              ) : (
                ""
              )
            }
          >
            <span>
              <Button
                onClick={handleSubmit}
                disabled={isDisabled}
                sx={{
                  width: 110,
                  height: 50,
                  borderRadius: "28px",
                  padding: "18px 0",
                  textTransform: "none",
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "16px",
                  lineHeight: "22px",
                  color: "#fff",
                  backgroundColor: isDisabled
                    ? theme.palette.custom.mainBlueDisable
                    : theme.palette.custom.mainBlue,

                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: isDisabled
                      ? theme.palette.custom.mainBlueDisable
                      : theme.palette.custom.mainBlue,
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    color: "#fff",
                    opacity: 1,
                  },
                }}
              >
                Confirm
              </Button>
            </span>
          </Tooltip>
        </Box>
        <Typography
          sx={{
            fontFamily: "Inter",
            fontWeight: 500,
            fontSize: "12px",
            lineHeight: "16px",
            letterSpacing: "0",
            color: theme.palette.custom.blueGray600,
          }}
        >
          {likePrice
            ? `At the moment your like amount is - ${formatMoney(likePrice)} USDC`
            : "Please use USDC when entering the price."}
        </Typography>
      </Box>
    </Box>
  );
};

export default LikePrice;
